import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "./firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { getUserData } from "./services/userService";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

// Toasts
import { ToastProvider } from "./components/Toast";

// Loading page
import Loading from "./pages/loading";

// Pages
import Login from "./pages/login";
import Home from "./pages/home";
import Form from "./pages/form";
import Sessions from "./pages/sessions";
import Quiz from "./pages/quiz";
import FormEntrance from "./pages/userentrance";
import QuizComplete from "./pages/quizComplete";
import ResultsDashboard from "./pages/results";
import AdminHome from "./pages/adminHome";
import QuizOverview from "./pages/QuizOverview";
import ForgotPassword from "./pages/forgotPassword";
import TermsOfServicePage from "./pages/termsofservice";
import PrivacyPolicyPage from "./pages/privacypolicy";

// Chatbot
import ChatbotPage from "./components/ChatBox";
import ProfilePage from "./pages/profile/page";
import SettingsPage from "./pages/settings/page";

// Payment Pages
import PaymentPage from "./stripe/paymentpage"; // Payment page in src/stripe
import PaymentSuccess from "./pages/paymentsuccess"; // Success page in src/files
import PaymentFailed from "./pages/paymentfailed"; // Failed page
import CorrectAnswers from "./pages/correctAnswers"; // Correct answers

// Protected route for admin/creator routes
const AdminRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          const userData = await getUserData(authUser.uid);
          setUser(userData);

          const fromRedirect = location.state?.fromRedirect;

          if (!fromRedirect) {
            if (userData?.adminStatus) {
              if (location.pathname === "/") {
                navigate("/admin", {
                  replace: true,
                  state: { fromRedirect: true },
                });
              }
            } else {
              if (location.pathname.startsWith("/admin")) {
                navigate("/", {
                  replace: true,
                  state: { fromRedirect: true },
                });
              }
            }
          }
        } else {
          setUser(null);
          navigate("/login", { replace: true }); // Redirect to login instead of landing
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, location]);

  if (loading)
    return (
      <Loading fullScreen type="default" theme="light" text="Loading ..." />
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Route for quiz that checks session storage for access token
const QuizRoute = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const generateRejoinCode = () => {
      return Math.random().toString(36).substring(2, 8).toUpperCase();
    };

    const checkAccess = async () => {
      const pathname = window.location.pathname;
      const sessionId = pathname.split("/quiz/")[1]?.split("/")[0];

      if (sessionId) {
        const accessToken = sessionStorage.getItem(`formAccess_${sessionId}`);

        if (!accessToken) {
          const currentUser = auth.currentUser;
          if (currentUser) {
            try {
              // Load session to retrieve formId
              const sessionRef = doc(db, "sessions", sessionId);
              const sessionSnap = await getDoc(sessionRef);
              if (!sessionSnap.exists()) {
                navigate(`/form-entrance/${sessionId}`);
                setLoading(false);
                return;
              }
              const sessionData = sessionSnap.data();
              const formId = sessionData.formId;

              // Try to reuse respondent for (sessionId, userId)
              const respondentsRef = collection(db, "respondents");
              const q = query(
                respondentsRef,
                where("sessionId", "==", sessionId),
                where("userId", "==", currentUser.uid)
              );
              const snap = await getDocs(q);

              let respondentId;
              let rejoinCode;
              if (!snap.empty) {
                const d = snap.docs[0];
                respondentId = d.id;
                rejoinCode = d.data().rejoinCode || generateRejoinCode();
                if (!d.data().rejoinCode) {
                  await setDoc(
                    doc(db, "respondents", respondentId),
                    { rejoinCode },
                    { merge: true }
                  );
                }
              } else {
                rejoinCode = generateRejoinCode();
                respondentId = `${Date.now()}-${Math.random()
                  .toString(36)
                  .substr(2, 9)}`;
                await setDoc(
                  doc(db, "respondents", respondentId),
                  {
                    name: currentUser.displayName || "User",
                    email: (currentUser.email || "").toLowerCase() || undefined,
                    userId: currentUser.uid,
                    rejoinCode,
                    createdAt: new Date(),
                    type: "user",
                    sessionId,
                    formId,
                    status: "active",
                  },
                  { merge: true }
                );
              }

              sessionStorage.setItem(`formAccess_${sessionId}`, rejoinCode);
              sessionStorage.setItem(`respondentId_${sessionId}`, respondentId);
              sessionStorage.setItem(`formId_${sessionId}`, formId);

              setHasAccess(true);
              setLoading(false);
              return;
            } catch (e) {
              console.error("Auto-provision access failed:", e);
            }
          }

          navigate(`/form-entrance/${sessionId}`);
        } else {
          setHasAccess(true);
        }
      }
      setLoading(false);
    };

    checkAccess();
  }, [navigate]);

  if (loading)
    return (
      <Loading fullScreen type="default" theme="light" text="Loading ..." />
    );
  if (!hasAccess) return null;
  return children;
};

// Wrapper for form entrance that skips entrance for already-authenticated users
const EntranceRoute = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [renderEntrance, setRenderEntrance] = useState(false);

  useEffect(() => {
    const generateRejoinCode = () =>
      Math.random().toString(36).substring(2, 8).toUpperCase();

    const handle = async () => {
      try {
        if (!sessionId) {
          setRenderEntrance(true);
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          setRenderEntrance(true);
          return;
        }

        // Ensure session exists to get formId
        const sessionRef = doc(db, "sessions", sessionId);
        const sessionSnap = await getDoc(sessionRef);
        if (!sessionSnap.exists()) {
          setRenderEntrance(true);
          return;
        }
        const sessionData = sessionSnap.data();
        const formId = sessionData.formId;

        // Reuse or create respondent for (sessionId, userId)
        const respondentsRef = collection(db, "respondents");
        const q = query(
          respondentsRef,
          where("sessionId", "==", sessionId),
          where("userId", "==", currentUser.uid)
        );
        const snap = await getDocs(q);

        let respondentId;
        let rejoinCode;
        if (!snap.empty) {
          const d = snap.docs[0];
          respondentId = d.id;
          rejoinCode = d.data().rejoinCode || generateRejoinCode();
          if (!d.data().rejoinCode) {
            await setDoc(
              doc(db, "respondents", respondentId),
              { rejoinCode },
              { merge: true }
            );
          }
        } else {
          rejoinCode = generateRejoinCode();
          respondentId = `${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          await setDoc(
            doc(db, "respondents", respondentId),
            {
              name: currentUser.displayName || "User",
              email: (currentUser.email || "").toLowerCase() || undefined,
              userId: currentUser.uid,
              rejoinCode,
              createdAt: new Date(),
              type: "user",
              sessionId,
              formId,
              status: "active",
            },
            { merge: true }
          );
        }

        sessionStorage.setItem(`formAccess_${sessionId}`, rejoinCode);
        sessionStorage.setItem(`respondentId_${sessionId}`, respondentId);
        sessionStorage.setItem(`formId_${sessionId}`, formId);

        navigate(`/quiz/${sessionId}`, { replace: true });
      } catch (e) {
        console.error("EntranceRoute auto-provision failed:", e);
        setRenderEntrance(true);
      } finally {
        setChecking(false);
      }
    };

    handle();
  }, [sessionId, navigate]);

  if (checking)
    return (
      <Loading fullScreen type="default" theme="light" text="Loading ..." />
    );

  if (renderEntrance) return <FormEntrance />;
  return null;
};

// Protected route for authenticated users (non-admin specific)
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          const userData = await getUserData(authUser.uid);
          setUser(userData);
        } else {
          setUser(null);
          navigate("/login", { replace: true }); // Redirect to login instead of landing
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading)
    return (
      <Loading fullScreen type="default" theme="light" text="Loading ..." />
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Auth checker for home route
const HomeRedirect = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (authUser) {
          const userData = await getUserData(authUser.uid);

          if (userData?.adminStatus) {
            navigate("/admin", { replace: true });
          } else {
            navigate("/home", { replace: true });
          }
        } else {
          navigate("/login", { replace: true }); // Redirect to login instead of landing
        }
      } catch (error) {
        console.error("Auth state change error:", error);
        navigate("/login", { replace: true }); // Redirect to login instead of landing
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (loading)
    return (
      <Loading fullScreen type="default" theme="light" text="Loading ..." />
    );
  return null;
};

function App() {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const backend = isMobile ? TouchBackend : HTML5Backend;

  return (
    <DndProvider backend={backend}>
      <ToastProvider>
        <Router>
          <Routes>
            {/* Root Route - Redirects based on auth state */}
            <Route path="/" element={<HomeRedirect />} />

            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/form-entrance/:sessionId"
              element={<EntranceRoute />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* Admin/User Routes */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forms/:formId"
              element={
                <AdminRoute>
                  <Form />
                </AdminRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminHome />
                </AdminRoute>
              }
            />
            <Route
              path="/sessions/:formId"
              element={
                <AdminRoute>
                  <Sessions />
                </AdminRoute>
              }
            />
            <Route
              path="/results/:formId"
              element={
                <AdminRoute>
                  <ResultsDashboard />
                </AdminRoute>
              }
            />

            {/* Quiz Routes */}
            <Route
              path="/quiz/:sessionId"
              element={
                <QuizRoute>
                  <Quiz />
                </QuizRoute>
              }
            />
            <Route
              path="/quiz/:sessionId/complete"
              element={<QuizComplete />}
            />
            <Route
              path="/quiz/:sessionId/correctAnswer"
              element={<CorrectAnswers />}
            />

            {/* Quiz Overview Route */}
            <Route
              path="/quiz-overview/:formId"
              element={
                <ProtectedRoute>
                  <QuizOverview />
                </ProtectedRoute>
              }
            />

            {/* Chatbot Route */}
            <Route path="/chatbot" element={<ChatbotPage />} />

            {/* Payment Routes */}
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-failed"
              element={
                <ProtectedRoute>
                  <PaymentFailed />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Redirect landing page requests to login */}
            <Route path="/landing" element={<Navigate to="/login" replace />} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ToastProvider>
    </DndProvider>
  );
}

export default App;
